import { useEffect, useMemo, useState } from 'react';
import type { Book, ShelfStatus } from '@booknest/shared';

import type { ShelfEntryView } from '../api/library.api.js';
import { useAddToShelf } from '../hooks/useAddToShelf.js';
import { useRemoveFromShelf } from '../hooks/useRemoveFromShelf.js';
import { useUpdateShelfEntry } from '../hooks/useUpdateShelfEntry.js';

import { BookCover } from './BookCover.js';

const STATUS_BUTTONS: ReadonlyArray<{
  status: ShelfStatus;
  icon: string;
  label: string;
}> = [
  { status: 'wishlist', icon: '💌', label: 'Wishlist' },
  { status: 'reading', icon: '🔖', label: 'En cours' },
  { status: 'done', icon: '✨', label: 'Terminé' },
  { status: 'abandoned', icon: '🍂', label: 'Abandonné' },
];

const DATE_FORMAT = new Intl.DateTimeFormat('fr-FR', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
});

interface BookModalProps {
  book: Book;
  entry: ShelfEntryView | null;
  onClose: () => void;
}

export function BookModal({ book, entry, onClose }: BookModalProps): JSX.Element {
  useEffect(() => {
    const onKey = (e: KeyboardEvent): void => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const releaseDate = book.releaseDate ? new Date(book.releaseDate) : null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="book-modal-title"
      onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
      className="modal-overlay-anim fixed inset-0 z-50 flex items-end justify-center bg-brown/55 p-0 backdrop-blur md:items-center md:p-6"
    >
      <div className="modal-content-anim flex max-h-[90vh] w-full max-w-xl flex-col overflow-y-auto rounded-t-3xl bg-cream-light shadow-xl md:max-h-[85vh] md:rounded-3xl">
        <Hero book={book} onClose={onClose} />
        <StatusActions book={book} entry={entry} onClose={onClose} />
        {entry && <PersonalRating entry={entry} />}
        {book.synopsis && <Synopsis text={book.synopsis} />}
        {book.tags.length > 0 && (
          <section className="px-6 pt-4 md:px-8">
            <h3 className="font-serif text-base font-semibold text-brown">Tags</h3>
            <ul className="mt-2 flex flex-wrap gap-1.5">
              {book.tags.map((tag) => (
                <li
                  key={tag}
                  className="rounded-full bg-paper px-3 py-1 text-xs font-medium text-brown-soft ring-1 ring-cream-dark"
                >
                  {tag}
                </li>
              ))}
            </ul>
          </section>
        )}
        <MetaGrid pages={book.pages} year={book.year} releaseDate={releaseDate} />
        {entry && <RemoveAction entry={entry} onClose={onClose} />}
      </div>
    </div>
  );
}

interface HeroProps {
  book: Book;
  onClose: () => void;
}

function Hero({ book, onClose }: HeroProps): JSX.Element {
  return (
    <div className="relative flex gap-5 bg-gradient-to-br from-rose-light to-cream-dark p-6 md:p-8">
      <button
        type="button"
        onClick={onClose}
        aria-label="Fermer"
        className="absolute right-4 top-4 flex h-9 w-9 items-center justify-center rounded-full bg-paper text-brown shadow-sm transition-colors hover:bg-cream-dark"
      >
        ✕
      </button>
      <BookCover
        url={book.coverUrl}
        title={book.title}
        book={book}
        className="w-28 flex-shrink-0 md:w-32"
      />
      <div className="flex flex-1 flex-col gap-1 pt-1 pr-10">
        {book.genre && (
          <span className="font-script text-base text-terracotta-dark">
            {book.genre}
          </span>
        )}
        <h2
          id="book-modal-title"
          className="font-serif text-2xl font-semibold leading-tight text-brown md:text-3xl"
        >
          {book.title}
        </h2>
        <p className="text-sm text-brown-soft">{book.author}</p>
        {book.rating !== null && (
          <Rating value={book.rating} count={book.ratingsCount} />
        )}
        {book.year !== null && (
          <p className="mt-1 text-xs text-brown-light">{book.year}</p>
        )}
      </div>
    </div>
  );
}

function Rating({
  value,
  count,
}: {
  value: number;
  count: number | null;
}): JSX.Element {
  const full = Math.floor(value);
  const half = value - full >= 0.5;
  const empty = 5 - full - (half ? 1 : 0);
  return (
    <p
      className="mt-1 flex items-center gap-1.5 text-sm text-brown"
      aria-label={`Note ${value.toFixed(1)} sur 5${count ? `, ${count} avis` : ''}`}
    >
      <span aria-hidden className="text-gold">
        {'★'.repeat(full)}
        {half && '⯨'}
        <span className="text-cream-dark">{'★'.repeat(empty)}</span>
      </span>
      <span className="font-semibold">{value.toFixed(1)}</span>
      {count !== null && count > 0 && (
        <span className="text-xs text-brown-light">({count.toLocaleString('fr-FR')} avis)</span>
      )}
    </p>
  );
}

interface StatusActionsProps {
  book: Book;
  entry: ShelfEntryView | null;
  onClose: () => void;
}

function StatusActions({ book, entry, onClose }: StatusActionsProps): JSX.Element {
  const addMutation = useAddToShelf();
  const updateMutation = useUpdateShelfEntry();

  const isUpdate = entry !== null;
  const currentStatus = entry?.status ?? null;
  const pending = addMutation.isPending || updateMutation.isPending;

  const handleStatusClick = (status: ShelfStatus): void => {
    if (status === currentStatus) return;
    if (isUpdate && entry) {
      updateMutation.mutate(
        { entryId: entry.id, status },
        { onSuccess: () => onClose() },
      );
    } else {
      addMutation.mutate(
        { bookId: book.id, status },
        { onSuccess: () => onClose() },
      );
    }
  };

  return (
    <section className="px-6 pt-5 md:px-8">
      <p className="font-serif text-sm font-semibold text-brown">
        {isUpdate ? 'Changer le statut' : 'Ajouter à ta bibliothèque'}
      </p>
      <div className="mt-3 grid grid-cols-2 gap-2 md:grid-cols-4">
        {STATUS_BUTTONS.map((b) => {
          const active = b.status === currentStatus;
          return (
            <button
              key={b.status}
              type="button"
              disabled={pending || active}
              onClick={() => handleStatusClick(b.status)}
              aria-pressed={active}
              className={`flex items-center justify-center gap-1.5 rounded-full px-3 py-2 text-xs font-semibold transition-colors ${
                active
                  ? 'bg-terracotta text-cream-light ring-1 ring-terracotta-dark'
                  : 'bg-paper text-brown ring-1 ring-cream-dark hover:bg-cream-dark disabled:opacity-50'
              }`}
            >
              <span aria-hidden>{b.icon}</span>
              <span>{b.label}</span>
            </button>
          );
        })}
      </div>
      {(addMutation.isError || updateMutation.isError) && (
        <p className="mt-2 text-xs text-red-500">
          Une erreur est survenue. Réessaie dans un instant.
        </p>
      )}
    </section>
  );
}

interface PersonalRatingProps {
  entry: ShelfEntryView;
}

function PersonalRating({ entry }: PersonalRatingProps): JSX.Element {
  const mutation = useUpdateShelfEntry();
  const [hovered, setHovered] = useState<number | null>(null);

  const current = entry.rating;
  const displayed = hovered ?? current ?? 0;

  const handleClick = (value: number): void => {
    // Click sur l'étoile actuelle = clear
    const next = current === value ? null : value;
    mutation.mutate({ entryId: entry.id, rating: next });
  };

  return (
    <section className="px-6 pt-5 md:px-8">
      <p className="font-serif text-sm font-semibold text-brown">Ma note</p>
      <div
        className="mt-2 flex items-center gap-2"
        onMouseLeave={() => setHovered(null)}
      >
        <div className="flex">
          {[1, 2, 3, 4, 5].map((star) => {
            const filled = star <= displayed;
            return (
              <button
                key={star}
                type="button"
                disabled={mutation.isPending}
                onClick={() => handleClick(star)}
                onMouseEnter={() => setHovered(star)}
                aria-label={`Noter ${star} sur 5`}
                aria-pressed={current === star}
                className={`-mx-0.5 px-0.5 text-2xl leading-none transition-transform hover:scale-110 disabled:opacity-50 ${
                  filled ? 'text-gold' : 'text-cream-dark'
                }`}
              >
                ★
              </button>
            );
          })}
        </div>
        {current !== null ? (
          <span className="text-xs text-brown-soft">
            {current} / 5 — re-clique sur l'étoile pour effacer
          </span>
        ) : (
          <span className="text-xs text-brown-light">Pas encore notée</span>
        )}
      </div>
      {mutation.isError && (
        <p className="mt-2 text-xs text-red-500">
          Impossible d'enregistrer la note.
        </p>
      )}
    </section>
  );
}

interface RemoveActionProps {
  entry: ShelfEntryView;
  onClose: () => void;
}

function RemoveAction({ entry, onClose }: RemoveActionProps): JSX.Element {
  const [confirming, setConfirming] = useState(false);
  const removeMutation = useRemoveFromShelf();

  const handleConfirm = (): void => {
    removeMutation.mutate(entry.id, { onSuccess: () => onClose() });
  };

  return (
    <section className="border-t border-cream-dark/60 px-6 py-5 md:px-8">
      {!confirming ? (
        <button
          type="button"
          onClick={() => setConfirming(true)}
          className="text-xs font-semibold text-brown-soft transition-colors hover:text-red-500"
        >
          🗑️ Retirer de ma bibliothèque
        </button>
      ) : (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <p className="text-sm text-brown">
            Retirer définitivement ce livre de ta bibliothèque ?
          </p>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => setConfirming(false)}
              disabled={removeMutation.isPending}
              className="rounded-full bg-paper px-3 py-1.5 text-xs font-semibold text-brown ring-1 ring-cream-dark hover:bg-cream-dark"
            >
              Annuler
            </button>
            <button
              type="button"
              onClick={handleConfirm}
              disabled={removeMutation.isPending}
              className="rounded-full bg-red-500 px-3 py-1.5 text-xs font-semibold text-cream-light hover:bg-red-600 disabled:opacity-50"
            >
              {removeMutation.isPending ? 'Suppression…' : 'Confirmer'}
            </button>
          </div>
        </div>
      )}
    </section>
  );
}

const SYNOPSIS_COLLAPSED_CHARS = 480;

function Synopsis({ text }: { text: string }): JSX.Element {
  const paragraphs = useMemo(() => cleanSynopsis(text), [text]);
  const full = paragraphs.join('\n\n');
  const isLong = full.length > SYNOPSIS_COLLAPSED_CHARS;
  const [expanded, setExpanded] = useState(false);

  const displayed =
    expanded || !isLong
      ? paragraphs
      : truncateAtSentence(full, SYNOPSIS_COLLAPSED_CHARS).split('\n\n');

  return (
    <section className="px-6 pt-5 md:px-8">
      <h3 className="font-serif text-base font-semibold text-brown">Résumé</h3>
      <div className="mt-2 flex flex-col gap-2.5 font-serif text-sm leading-relaxed text-brown-soft md:text-[15px]">
        {displayed.map((p, i) => (
          <p key={i} className="hyphens-auto text-justify">
            {p}
          </p>
        ))}
      </div>
      {isLong && (
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          className="mt-2 text-xs font-semibold text-terracotta hover:underline"
        >
          {expanded ? 'Voir moins' : 'Voir plus'}
        </button>
      )}
    </section>
  );
}

function cleanSynopsis(raw: string): ReadonlyArray<string> {
  const withoutTags = raw
    .replace(/<br\s*\/?>(\s*<br\s*\/?>)*/gi, '\n\n')
    .replace(/<\/p\s*>/gi, '\n\n')
    .replace(/<[^>]+>/g, '')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&quot;/g, '"')
    .replace(/&#39;|&apos;/g, "'")
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>');

  return withoutTags
    .split(/\n\s*\n+/)
    .map((p) => p.replace(/\s+/g, ' ').trim())
    .filter((p) => p.length > 0);
}

function truncateAtSentence(text: string, max: number): string {
  if (text.length <= max) return text;
  const slice = text.slice(0, max);
  const lastStop = Math.max(
    slice.lastIndexOf('. '),
    slice.lastIndexOf('! '),
    slice.lastIndexOf('? '),
    slice.lastIndexOf('… '),
  );
  const cut = lastStop > max * 0.5 ? lastStop + 1 : slice.lastIndexOf(' ');
  return `${slice.slice(0, cut > 0 ? cut : max).trimEnd()}…`;
}

interface MetaGridProps {
  pages: number | null;
  year: number | null;
  releaseDate: Date | null;
}

function MetaGrid({ pages, year, releaseDate }: MetaGridProps): JSX.Element {
  return (
    <section className="grid grid-cols-3 gap-3 px-6 py-5 md:px-8 md:py-6">
      <MetaItem label="Pages" value={pages !== null ? String(pages) : '—'} />
      <MetaItem label="Année" value={year !== null ? String(year) : '—'} />
      <MetaItem
        label="Sortie"
        value={releaseDate ? DATE_FORMAT.format(releaseDate) : '—'}
      />
    </section>
  );
}

function MetaItem({ label, value }: { label: string; value: string }): JSX.Element {
  return (
    <div className="rounded-2xl bg-paper p-3 text-center shadow-sm">
      <p className="font-serif text-sm font-semibold text-brown">{value}</p>
      <p className="mt-0.5 text-[10px] font-semibold uppercase tracking-wider text-brown-light">
        {label}
      </p>
    </div>
  );
}
